import { N8nModule } from '../../types';

export const module2: N8nModule = {
  id: 'n8n-core-concepts',
  title: 'Core n8n Concepts',
  description: 'Master the core concepts of n8n workflows including nodes, connections, data flow, and error handling.',
  duration: '2 hours',
  objectives: [
    'Master different node types and their specific use cases',
    'Understand data flow between nodes and JSON structures',
    'Use expressions and variables for dynamic workflows',
    'Implement error handling and retry logic',
    'Work with webhook and schedule triggers effectively'
  ],
  content: `# Core n8n Concepts

## Node Types Deep Dive

Understanding node types is fundamental to building effective n8n workflows. Each node type serves a specific purpose in the automation pipeline.

### Trigger Nodes

Trigger nodes **start** your workflow execution. They listen for events or run on schedules.

#### Webhook Trigger

The webhook trigger creates an HTTP endpoint that external systems can call to start your workflow.

**Configuration:**

\`\`\`json
{
  "httpMethod": "POST",
  "path": "my-webhook",
  "responseMode": "onReceived",
  "authentication": "headerAuth"
}
\`\`\`

**Use Cases:**
- Form submissions from websites
- GitHub webhooks (push, pull request events)
- Stripe payment notifications
- Custom API integrations

**Testing Webhooks:**

\`\`\`bash
# Get the webhook URL from n8n
WEBHOOK_URL="https://your-n8n.com/webhook/my-webhook"

# Send test POST request
curl -X POST $WEBHOOK_URL \\
  -H "Content-Type: application/json" \\
  -d '{"name": "John", "email": "john@example.com"}'
\`\`\`

💡 **Security Tip**: Always enable authentication on production webhooks using header auth or basic auth.

#### Schedule Trigger

Execute workflows on a time-based schedule using cron expressions.

**Common Patterns:**

\`\`\`javascript
// Every day at 9:00 AM
0 9 * * *

// Every hour at minute 30
30 * * * *

// Every Monday at 8:00 AM
0 8 * * 1

// Every 15 minutes
*/15 * * * *

// First day of every month at midnight
0 0 1 * *
\`\`\`

**Use Cases:**
- Daily report generation
- Periodic data sync
- Scheduled backups
- Reminder notifications
- Health check pings

#### Email Trigger

Monitor an email inbox and trigger workflows when emails arrive.

**Configuration:**
- **IMAP Server**: imap.gmail.com
- **Port**: 993 (SSL)
- **Folder**: INBOX
- **Check Interval**: Every 5 minutes

**Use Cases:**
- Customer support ticket creation
- Order processing from email notifications
- Document processing automation
- Email-based approvals

### Regular Nodes

Regular nodes process, transform, and route data through your workflow.

#### HTTP Request Node

The most versatile node for calling external APIs.

**GET Request Example:**

\`\`\`json
{
  "method": "GET",
  "url": "https://api.github.com/users/{{$json.username}}",
  "authentication": "genericCredentialType",
  "genericAuthType": "httpHeaderAuth",
  "headers": {
    "Accept": "application/vnd.github.v3+json"
  }
}
\`\`\`

**POST Request with JSON Body:**

\`\`\`json
{
  "method": "POST",
  "url": "https://api.example.com/users",
  "body": {
    "name": "{{$json.name}}",
    "email": "{{$json.email}}",
    "role": "user"
  },
  "headers": {
    "Content-Type": "application/json",
    "Authorization": "Bearer {{$credentials.apiKey}}"
  }
}
\`\`\`

#### Function Node

Execute custom JavaScript code for complex transformations.

**Basic Transformation:**

\`\`\`javascript
// Access input data
const items = $input.all();

// Transform each item
return items.map(item => {
  const data = item.json;
  
  return {
    json: {
      fullName: \`\${data.firstName} \${data.lastName}\`,
      email: data.email.toLowerCase(),
      createdAt: new Date().toISOString(),
      isActive: true
    }
  };
});
\`\`\`

**Advanced Example with Error Handling:**

\`\`\`javascript
const items = $input.all();
const results = [];

for (const item of items) {
  try {
    const data = item.json;
    
    // Validate required fields
    if (!data.email || !data.name) {
      throw new Error('Missing required fields');
    }
    
    // Process data
    const processed = {
      id: data.id,
      email: data.email.trim().toLowerCase(),
      name: data.name.trim(),
      score: calculateScore(data),
      timestamp: Date.now()
    };
    
    results.push({ json: processed });
  } catch (error) {
    // Log error but continue processing
    console.error('Error processing item:', error.message);
    results.push({ 
      json: { 
        error: error.message,
        originalData: item.json 
      } 
    });
  }
}

function calculateScore(data) {
  return (data.points || 0) * 1.5 + (data.bonus || 0);
}

return results;
\`\`\`

#### IF Node

Conditional branching based on data values.

**Simple Condition:**

\`\`\`json
{
  "conditions": {
    "number": [
      {
        "value1": "={{$json.age}}",
        "operation": "larger",
        "value2": 18
      }
    ]
  }
}
\`\`\`

**Multiple Conditions (AND logic):**

\`\`\`json
{
  "conditions": {
    "string": [
      {
        "value1": "={{$json.status}}",
        "operation": "equals",
        "value2": "active"
      }
    ],
    "boolean": [
      {
        "value1": "={{$json.isPremium}}",
        "operation": "true"
      }
    ]
  },
  "combineOperation": "all"
}
\`\`\`

#### Switch Node

Route data to multiple paths based on different conditions.

**Example: Order Priority Routing:**

\`\`\`json
{
  "mode": "rules",
  "rules": [
    {
      "name": "High Priority",
      "condition": "={{$json.total > 1000}}"
    },
    {
      "name": "Medium Priority",
      "condition": "={{$json.total > 100 && $json.total <= 1000}}"
    },
    {
      "name": "Low Priority",
      "condition": "={{$json.total <= 100}}"
    }
  ]
}
\`\`\`

### Output Nodes

Output nodes send data to external systems or services.

#### Send Email Node

\`\`\`json
{
  "fromEmail": "notifications@company.com",
  "toEmail": "={{$json.customerEmail}}",
  "subject": "Order Confirmation #{{$json.orderId}}",
  "text": "Thank you for your order! Order Details - Total: \${{$json.total}} Status: {{$json.status}}",
  "html": "<h1>Order Confirmation</h1><p>Thank you!</p>"
}
\`\`\`

#### Slack Node

\`\`\`json
{
  "channel": "#alerts",
  "text": "🚨 High-priority alert\\n\\nIssue: {{$json.issue}}\\nSeverity: {{$json.severity}}\\nTime: {{$now}}",
  "attachments": [
    {
      "color": "#ff0000",
      "title": "Alert Details",
      "text": "{{$json.description}}"
    }
  ]
}
\`\`\`

---

## Data Flow and JSON Structures

### Understanding \`$json\`

The \`$json\` variable contains data from the previous node.

**Example Data Flow:**

\`\`\`javascript
// Node 1: HTTP Request returns:
{
  "id": 123,
  "name": "John Doe",
  "email": "john@example.com",
  "orders": [
    { "id": 1, "total": 50 },
    { "id": 2, "total": 75 }
  ]
}

// Node 2: Function node can access:
const userId = $json.id;              // 123
const userName = $json.name;          // "John Doe"
const firstOrder = $json.orders[0];   // { id: 1, total: 50 }
const orderCount = $json.orders.length; // 2
\`\`\`

### Accessing Specific Nodes

Reference data from any node in the workflow:

\`\`\`javascript
// Access data from a specific node
const apiData = $node["HTTP Request"].json;
const webhookData = $node["Webhook"].json;

// Access the first item from a node
const firstItem = $node["HTTP Request"].json[0];

// Access binary data
const fileData = $node["Read Binary File"].binary;
\`\`\`

### Working with Arrays

When a node returns multiple items:

\`\`\`javascript
// $input.all() returns array of all items
const items = $input.all();

// Process each item
const processed = items.map(item => {
  return {
    json: {
      id: item.json.id,
      processed: true,
      timestamp: Date.now()
    }
  };
});

return processed;
\`\`\`

### Pinned Data for Testing

**Why Pin Data?**
- Test workflows without re-executing slow nodes
- Consistent test data during development
- Speed up debugging

**How to Pin:**
1. Execute workflow once
2. Click on node showing output
3. Click "Pin data" in data panel
4. Modify pinned data as needed for testing

---

## Expressions and Variables

Expressions allow dynamic content using double curly braces: \`{{ }}\`

### Basic Expression Syntax

\`\`\`javascript
// Simple value access
{{ $json.fieldName }}

// With JavaScript expression
{{ $json.price * 1.2 }}

// String concatenation
{{ $json.firstName + ' ' + $json.lastName }}

// Conditional (ternary)
{{ $json.age >= 18 ? 'Adult' : 'Minor' }}
\`\`\`

### Built-in Variables

**\`$json\`** - Data from previous node
\`\`\`javascript
{{ $json.email }}
{{ $json.user.profile.age }}
\`\`\`

**\`$node\`** - Access specific node data
\`\`\`javascript
{{ $node["HTTP Request"].json.result }}
\`\`\`

**\`$env\`** - Environment variables
\`\`\`javascript
{{ $env.API_KEY }}
{{ $env.DATABASE_URL }}
\`\`\`

**\`$now\`** - Current timestamp
\`\`\`javascript
{{ $now }}
{{ $now.toISO() }}
\`\`\`

**\`$today\`** - Today's date
\`\`\`javascript
{{ $today }}
\`\`\`

### Built-in Functions

**Date/Time Functions:**

\`\`\`javascript
// Format date
{{ $now.format('YYYY-MM-DD HH:mm:ss') }}

// Add/subtract time
{{ $now.plus(7, 'days').toISO() }}
{{ $now.minus(1, 'hour').toISO() }}

// Parse date string
{{ DateTime.fromISO($json.dateString) }}
\`\`\`

**String Functions:**

\`\`\`javascript
// Uppercase/lowercase
{{ $json.email.toUpperCase() }}
{{ $json.name.toLowerCase() }}

// Trim whitespace
{{ $json.input.trim() }}

// Replace
{{ $json.text.replace('old', 'new') }}

// Split and join
{{ $json.tags.split(',').join(', ') }}
\`\`\`

**Number Functions:**

\`\`\`javascript
// Math operations
{{ Math.round($json.price) }}
{{ Math.floor($json.value) }}
{{ Math.ceil($json.quantity / 10) }}

// Number formatting
{{ $json.amount.toFixed(2) }}
{{ Number($json.stringNumber) }}
\`\`\`

**Array Functions:**

\`\`\`javascript
// Array length
{{ $json.items.length }}

// Filter array
{{ $json.orders.filter(o => o.status === 'completed') }}

// Map array
{{ $json.users.map(u => u.email) }}

// Find in array
{{ $json.products.find(p => p.id === 123) }}
\`\`\`

### Complex Expression Example

\`\`\`javascript
// Calculate order total with tax and discount
{{ 
  (() => {
    const subtotal = $json.items.reduce((sum, item) => 
      sum + (item.price * item.quantity), 0
    );
    const discount = $json.discountPercent || 0;
    const tax = 0.08;
    
    const afterDiscount = subtotal * (1 - discount / 100);
    const total = afterDiscount * (1 + tax);
    
    return total.toFixed(2);
  })()
}}
\`\`\`

---

## Error Handling

### Try-Catch in Function Nodes

\`\`\`javascript
const items = $input.all();
const results = [];
const errors = [];

for (const item of items) {
  try {
    // Attempt risky operation
    const result = processItem(item.json);
    results.push({ json: result });
  } catch (error) {
    // Capture error for later processing
    errors.push({
      json: {
        error: error.message,
        input: item.json,
        timestamp: new Date().toISOString()
      }
    });
  }
}

// Return both results and errors
if (errors.length > 0) {
  console.error(\`\${errors.length} items failed\`);
}

return results.length > 0 ? results : errors;

function processItem(data) {
  if (!data.email) throw new Error('Email required');
  return { processed: true, email: data.email };
}
\`\`\`

### Error Workflows

Create dedicated error handling workflows:

1. Create new workflow named "Error Handler"
2. Add Webhook trigger
3. Process and log errors
4. Send notifications

**Calling Error Workflow:**

\`\`\`javascript
// In main workflow Function node
try {
  // Main processing
  return processData($json);
} catch (error) {
  // Call error workflow
  await $http.request({
    method: 'POST',
    url: $env.ERROR_WEBHOOK_URL,
    body: {
      workflowName: $workflow.name,
      error: error.message,
      data: $json,
      timestamp: new Date().toISOString()
    }
  });
  
  throw error; // Re-throw to mark node as failed
}
\`\`\`

### Node-Level Error Handling

Configure retry logic on individual nodes:

**HTTP Request Retry Settings:**
- **Max Retries**: 3
- **Wait Between Retries**: 1000ms (exponential backoff)
- **Error on Failure**: false (continue workflow even if fails)

### Error Output Path

Some nodes have error output paths:

1. Add IF node after risky node
2. Check for error: \`{{ $json.error !== undefined }}\`
3. Route errors to notification/logging
4. Route success to continue workflow

---

## Key Takeaways

- **Node Types**: Triggers start workflows, Regular nodes process data, Output nodes send results
- **Data Flow**: Use \`$json\` for previous node, \`$node\` for specific nodes, \`$env\` for environment
- **Expressions**: Double curly braces \`{{ }}\` enable dynamic content and JavaScript
- **Error Handling**: Try-catch in Functions, error workflows, retry logic, and error paths
- **Testing**: Pin data speeds up development and debugging
- **Best Practices**: Always validate input, handle errors gracefully, use descriptive node names

Practice these concepts in the next module where we build practical, real-world workflows!`,
  
  quickCheck: {
    questions: [
      {
        id: 'q1-trigger-purpose',
        question: "What is the primary purpose of a Trigger node?",
        options: [
          "To transform data in the workflow",
          "To start workflow execution based on events or schedules",
          "To send data to external systems",
          "To handle errors in the workflow"
        ],
        correctAnswer: 1,
        explanation: "Trigger nodes are designed to start workflow execution. They listen for events (like webhooks or emails) or run on schedules (cron jobs)."
      },
      {
        id: 'q2-data-access',
        question: "Which variable should you use to access data from the previous node?",
        options: [
          "$node",
          "$previous",
          "$json",
          "$data"
        ],
        correctAnswer: 2,
        explanation: "$json contains the data output from the previous node in the workflow. This is the most common variable for accessing pipeline data."
      },
      {
        id: 'q3-env-variables',
        question: "How do you access environment variables in n8n expressions?",
        options: [
          "{{ process.env.VARIABLE }}",
          "{{ $env.VARIABLE }}",
          "{{ ENV.VARIABLE }}",
          "{{ getEnv('VARIABLE') }}"
        ],
        correctAnswer: 1,
        explanation: "Environment variables are accessed using $env followed by the variable name: {{ $env.VARIABLE_NAME }}"
      },
      {
        id: 'q4-pinned-data-usage',
        question: "What does 'pinned data' allow you to do?",
        options: [
          "Save workflows permanently",
          "Test workflows with sample data without re-executing previous nodes",
          "Share data between workflows",
          "Encrypt sensitive data"
        ],
        correctAnswer: 1,
        explanation: "Pinned data lets you lock sample output from a node, allowing faster testing by skipping re-execution of earlier workflow steps."
      },
      {
        id: 'q5-function-node',
        question: "Which node type is best for complex data transformations using JavaScript?",
        options: [
          "HTTP Request node",
          "IF node",
          "Function node",
          "Switch node"
        ],
        correctAnswer: 2,
        explanation: "The Function node allows you to write custom JavaScript code for complex transformations, calculations, and data manipulation."
      },
      {
        id: 'q6-cron-expression',
        question: "What is the correct cron expression for 'Every day at 9:00 AM'?",
        options: [
          "0 9 * * *",
          "9 0 * * *",
          "* 9 * * *",
          "0 0 9 * *"
        ],
        correctAnswer: 0,
        explanation: "Cron format is minute hour day month weekday. The expression 0 9 * * * means minute 0, hour 9, every day, every month, every weekday."
      },
      {
        id: 'q7-node-reference',
        question: "How can you reference data from a specific node named 'HTTP Request' in your workflow?",
        options: [
          "{{ $json.HTTPRequest }}",
          "{{ $node['HTTP Request'].json }}",
          "{{ nodes.HTTPRequest }}",
          "{{ getData('HTTP Request') }}"
        ],
        correctAnswer: 1,
        explanation: "Use $node['Node Name'].json to access data from a specific node by its name, regardless of its position in the workflow."
      }
    ]
  }
};
